<!--
  SchemaForm.vue — the thin wrapper around the device-settings schema-form engine
  (Engine A flagship). In the deployed app this is `device-schema-portlet`, whose
  body is:

      VJsf(schema=resultSchema, options=options, v-model=model)   (@koumoul/vjsf)

  with x-display:"custom-*" nodes routed to 9 custom Vuetify widgets via VJsf's
  named scoped slots (see field-types.js CUSTOM_WIDGET_REGISTRY). VJsf is a large
  vendored dependency bundled in DeviceSettingsSchemaPortlet.js; when it is
  installed, drop it into the `#engine` slot below and it takes over rendering.

  Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
  (props schema/apiMethod/trackerId/localeRoot/saveRootProp/readonly; resultSchema
  normalization; save POSTs {tracker_id, ...model} OR {tracker_id,[saveRootProp]:model}
  to schema.apiMethod; x-if visibility; JSON-Schema -> Vuetify rule compiler),
  internal impl re-authored. The VJsf-less path renders a faithful FALLBACK from
  the SAME field registry so schemas still render off the shelf.
-->
<template>
  <v-form ref="vform" v-model="valid" @submit.prevent="saveData">
    <!-- Real engine goes here in device-settings; host injects the VJsf engine -->
    <slot
      name="engine"
      :schema="resultSchema"
      :options="options"
      :model="model"
      :on-input="onModelInput"
    >
      <!-- FALLBACK renderer: shared field registry, no VJsf dependency -->
      <div v-if="resultTitle" class="title mb-3">{{ resultTitle }}</div>
      <template v-for="leaf in leaves">
        <component
          :is="componentFor(leaf.node)"
          :key="leaf.path"
          :value="getPath(model, leaf.path)"
          :field="fieldFor(leaf)"
          :rules="rulesFor(leaf)"
          :disabled="readonly"
          @input="setLeaf(leaf, $event)"
        />
      </template>
      <div v-if="!leaves.length" class="grey--text pa-4 text-xs-center">
        {{ emptyText }}
      </div>
    </slot>

    <div class="mt-3 text-xs-right">
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="readonly || !valid"
        @click="saveData"
      >
        {{ saveLabel }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { schemaWidgetName } from './field-types.js';
import {
  makeResultSchema,
  buildRules,
  buildMessages,
  evalExpr,
  makeExprCtx,
} from './schema-engine.js';
import { request } from './entity-api.js';
import EntityInputText from './inputs/EntityInputText.vue';
import EntityInputSelect from './inputs/EntityInputSelect.vue';
import EntityInputDate from './inputs/EntityInputDate.vue';
import EntityInputTags from './inputs/EntityInputTags.vue';

/* inline boolean fallback (the real engine uses schema-radio / v-switch) */
const SchemaFallbackSwitch = {
  name: 'schema-fallback-switch',
  props: { value: { default: null }, field: { type: Object, default: () => ({}) }, disabled: Boolean, rules: { type: Array, default: () => [] } },
  render(h) {
    return h('v-switch', {
      props: { inputValue: this.value, label: (this.field && this.field.label) || '', disabled: this.disabled },
      on: { change: (val) => this.$emit('input', val) },
    });
  },
};

export default {
  name: 'schema-form',
  components: {
    'entity-input-text': EntityInputText,
    'entity-input-select': EntityInputSelect,
    'entity-input-date': EntityInputDate,
    'entity-input-tags': EntityInputTags,
    'schema-fallback-switch': SchemaFallbackSwitch,
  },
  props: {
    schema: { type: Object, default: () => ({}) },
    value: { type: Object, default: () => ({}) },
    apiMethod: { type: String, default: '' },
    trackerId: { default: null },
    localeRoot: { type: String, default: '' },
    resultLocalePart: { type: String, default: '' },
    saveRootProp: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    saveLabel: { type: String, default: 'Save' },
    emptyText: { type: String, default: 'Empty schema' },
  },
  data() {
    return { model: Object.assign({}, this.value), valid: true, saving: false };
  },
  computed: {
    options() {
      return { messages: buildMessages(this.$t ? this.$t.bind(this) : null) };
    },
    resultSchema() {
      if (!this.schema || !Object.keys(this.schema).length) return {};
      return makeResultSchema(this.schema, {
        $t: this.$t ? this.$t.bind(this) : undefined,
        $te: this.$te ? this.$te.bind(this) : undefined,
        localeRoot: this.localeRoot,
        resultLocalePart: this.resultLocalePart || this.localeRoot,
      });
    },
    resultApiMethod() {
      return (this.resultSchema && this.resultSchema.apiMethod) || this.apiMethod;
    },
    resultTitle() {
      return this.resultSchema && this.resultSchema.title;
    },
    isEmptySchema() {
      return !this.resultSchema || !Object.keys(this.resultSchema).length;
    },
    /* flatten schema -> visible leaf nodes with dotted paths (x-if honored) */
    leaves() {
      const out = [];
      const walk = (node, path) => {
        if (!node || typeof node !== 'object') return;
        if (node['x-display'] === 'hidden') return;
        if (node['x-if'] && !evalExpr(node['x-if'], makeExprCtx(this.model))) return;
        if (node.type === 'object' && node.properties) {
          Object.keys(node.properties).forEach((k) => walk(node.properties[k], path ? path + '.' + k : k));
          return;
        }
        if (path) out.push({ path, node });
      };
      const root = this.resultSchema;
      if (root && root.type === 'object' && root.properties) {
        Object.keys(root.properties).forEach((k) => walk(root.properties[k], k));
      }
      return out;
    },
  },
  watch: {
    value: { deep: true, handler(next) { this.model = Object.assign({}, next); } },
  },
  methods: {
    getPath(obj, path) {
      return String(path).split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
    },
    setPath(obj, path, val) {
      const keys = String(path).split('.');
      const last = keys.pop();
      let cur = obj;
      keys.forEach((k) => { if (cur[k] == null || typeof cur[k] !== 'object') this.$set(cur, k, {}); cur = cur[k]; });
      this.$set(cur, last, val);
    },
    componentFor(node) {
      const name = schemaWidgetName(node); // custom-* -> schema-* ; else entity-input-*
      if (node.type === 'boolean') return 'schema-fallback-switch';
      if (node.type === 'array') return 'entity-input-tags';
      if (node.enum || node.oneOf) return 'entity-input-select';
      if (node['x-display'] === 'date-field' || node['x-display'] === 'custom-date-field') return 'entity-input-date';
      // Only the widgets registered on this component are safe to return; everything else
      // (integer/number/string, and any custom-* widget we don't ship) renders through the
      // text input, which honours field.type for numeric fields.
      const REGISTERED = { 'entity-input-text': 1, 'entity-input-select': 1, 'entity-input-date': 1, 'entity-input-tags': 1 };
      return REGISTERED[name] ? name : 'entity-input-text';
    },
    fieldFor(leaf) {
      const n = leaf.node;
      let type = 'text';
      if (n.type === 'integer') type = 'integer';
      else if (n.type === 'number') type = 'decimal';
      else if (n.type === 'array') type = 'tags';
      const items = (n.enum || (n.oneOf || []).map((o) => ({ id: o.const, label: o.title })))
        .map((e) => (typeof e === 'object' ? e : { id: e, label: String(e) }));
      return {
        id: leaf.path,
        label: n.title || leaf.path,
        type,
        required: !!n.required,
        params: { items, itemValue: 'id', itemText: 'label' },
      };
    },
    rulesFor(leaf) {
      return buildRules(leaf.node, this.options, !!leaf.node.required);
    },
    setLeaf(leaf, val) {
      this.setPath(this.model, leaf.path, val);
      this.onModelInput(this.model);
    },
    onModelInput(model) {
      this.model = model;
      this.$emit('input', Object.assign({}, model));
    },
    /* Save POSTs to the endpoint named INSIDE the schema (schema.apiMethod). */
    async saveData() {
      if (this.$refs.vform && !this.$refs.vform.validate()) return;
      if (!this.resultApiMethod) return;
      let body = { tracker_id: this.trackerId };
      if (this.saveRootProp) body[this.saveRootProp] = this.model;
      else body = Object.assign({}, body, this.model);
      this.saving = true;
      try {
        const resp = await request(this.resultApiMethod, body);
        this.$emit('saved', resp);
        return resp;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
